'use client';

/**
 * Translation (i18n) System for Cray Theme
 *
 * Provides internationalization support with locale management,
 * translation loading, and a React hook for easy use in components.
 */
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { useConfig } from '@/core/config/manager';
import { logger } from '@/lib/logger';

export type SupportedLocale = 'en' | 'es';

export type Translations = Record<string, any> & {
  app: {
    name: string;
    welcome: string;
    description: string;
  };
  navigation: {
    home: string;
    products: string;
    categories: string;
    cart: string;
    account: string;
    login: string;
    logout: string;
    search: string;
    deals: string;
    newArrivals: string;
    compare: string;
    blog: string;
    support: string;
    trackOrder: string;
  };
  dashboard: {
    title: string;
    welcome: string;
    stats: {
      totalSales: string;
      totalOrders: string;
      totalCustomers: string;
      lowStockProducts: string;
    };
    recentOrders: string;
    viewAllOrders: string;
  };
  common: {
    loading: string;
    error: string;
    success: string;
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    search: string;
    filter: string;
    sort: string;
    viewAll: string;
    learnMore: string;
    close: string;
    submit: string;
    back: string;
    next: string;
  };
  product: {
    addToCart: string;
    outOfStock: string;
    price: string;
    quantity: string;
    viewDetails: string;
    reviews: string;
    writeReview: string;
    inStock: string;
    specifications: string;
    description: string;
  };
  cart: {
    title: string;
    empty: string;
    total: string;
    subtotal: string;
    tax: string;
    shipping: string;
    checkout: string;
    continueShopping: string;
    removeItem: string;
    updateQuantity: string;
  };
  categories: {
    title: string;
    all: string;
    viewCategory: string;
    productsCount: string;
  };
  account: {
    dashboard: string;
    profile: string;
    orders: string;
    addresses: string;
    wishlist: string;
    paymentMethods: string;
    settings: string;
    signOut: string;
    myAccount: string;
    welcomeBack: string;
    totalOrders: string;
    pendingOrders: string;
    totalSpent: string;
    wishlistItems: string;
    manageInfo: string;
    viewManageReturns: string;
    savedAddresses: string;
    totalOrdersCount: string;
    profileSettings: string;
    orderHistory: string;
    viewTrackOrders: string;
    noOrdersFound: string;
    noOrdersYet: string;
    noOrdersFilter: string;
    startShopping: string;
    all: string;
    pending: string;
    processing: string;
    shipped: string;
    delivered: string;
    cancelled: string;
    orderNumber: string;
    status: string;
    date: string;
    total: string;
    items: string;
    viewDetails: string;
    trackOrder: string;
    managePersonalInfo: string;
    editProfile: string;
    name: string;
    email: string;
    phone: string;
    documentType: string;
    documentNumber: string;
    preferences: string;
    newsletter: string;
    notifications: string;
    language: string;
    profileUpdated: string;
    updateError: string;
    requiredField: string;
    manageShippingBilling: string;
    addAddress: string;
    editAddress: string;
    newAddress: string;
    streetAddress: string;
    apartment: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    deleteAddress: string;
    setDefault: string;
    default: string;
    shipping: string;
    billing: string;
    savedItems: string;
    itemsCount: string;
    emptyWishlist: string;
    saveItemsLater: string;
    addToCart: string;
    remove: string;
    inStock: string;
    outOfStock: string;
    managePaymentOptions: string;
    addPaymentMethod: string;
    removePaymentMethod: string;
    paymentSecure: string;
    paymentDemo: string;
    returnsRefunds: string;
    manageReturns: string;
    requestReturn: string;
    returnPolicy: string;
    noReturns: string;
    noReturnsYet: string;
    returnNumber: string;
    refundAmount: string;
    reason: string;
    returnStatuses: {
      pending: string;
      approved: string;
      rejected: string;
      refunded: string;
    };
    orderNotFound: string;
    orderNotFoundMessage: string;
    backToOrders: string;
    placedOn: string;
    orderItems: string;
    sku: string;
    qty: string;
    orderSummary: string;
    subtotal: string;
    paymentMethod: string;
    returns: string;
    personalInformation: string;
    fullName: string;
    cancel: string;
    saveChanges: string;
    saving: string;
    receiveNewsletter: string;
    receiveNotifications: string;
    placedOnDate: string;
    orderNumberLabel: string;
    moreItems: string;
    approved: string;
    rejected: string;
    paid: string;
    failed: string;
    in_transit: string;
    mp_processing: string;
    mp_approved: string;
    mp_pending: string;
    mp_rejected: string;
    oca_shipping_pending: string;
    oca_shipping_confirmed: string;
    oca_shipping_completed: string;
    oca_shipping_cancelled: string;
    shipping_pending: string;
    shipping_confirmed: string;
    shipping_completed: string;
    shipping_cancelled: string;
    freeShipping: string;
    discount: string;
    paymentHistory: string;
    paymentId: string;
    provider: string;
    shippingProvider: string;
    estimatedDelivery: string;
    confirmed: string;
    completed: string;
    abandoned: string;
    refunded: string;
    firstName: string;
    lastName: string;
    idType: string;
    idNumber: string;
    enterprise: string;
    taxId: string;
    birthdate: string;
    notes: string;
    customerType: string;
    username: string;
    viewInvoice: string;
    selectOption: string;
    nationalId: string;
    passport: string;
    other: string;
    selectType: string;
    regular: string;
    wholesaler: string;
    reseller: string;
  };
  auth: {
    signIn: string;
    signUp: string;
    createAccount: string;
    forgotPassword: {
      title: string;
      description: string;
      errorSendingEmail: string;
      emailSent: string;
      emailSentMessage: string;
      backToLogin: string;
      sending: string;
      sendResetLink: string;
    };
    resetPassword: {
      title: string;
      description: string;
      passwordMismatch: string;
      passwordTooShort: string;
      updateSuccess: string;
      updateError: string;
      passwordUpdated: string;
      passwordUpdatedMessage: string;
      newPassword: string;
      confirmNewPassword: string;
      updating: string;
      updatePassword: string;
    };
    verifyEmail: {
      verifying: string;
      verifyingMessage: string;
      verified: string;
      verifiedMessage: string;
      invalidToken: string;
      verificationSuccess: string;
      verificationError: string;
      backToLogin: string;
    };
    email: string;
    password: string;
    confirmPassword: string;
    rememberMe: string;
    wholesaler: string;
    reseller: string;
    loggedin: string;
  };
  checkout: {
    title: string;
    shipping: string;
    payment: string;
    review: string;
    placeOrder: string;
    orderSummary: string;
    shippingAddress: string;
    billingAddress: string;
    creditCard: string;
    paypal: string;
    bankTransfer: string;
    debitCard: string;
  };
  footer: {
    shop: string;
    myAccount: string;
    customerSupport: string;
    company: string;
    legal: string;
    followUs: string;
    newsletter: string;
    subscribeNewsletter: string;
  };
};

interface TranslationContextType {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: string) => string;
  translations: Translations;
  isLoading: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

/**
 * Hook to access translations in components
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { t, locale, setLocale } = useTranslation();
 *
 *   return (
 *     <div>
 *       <h1>{t.app.welcome}</h1>
 *       <button onClick={() => setLocale('es')}>Español</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within TranslationProvider');
  }
  return {
    t: (key: string) => getTranslationValue(context.translations, key), // Return the full translations object as 't' for convenience
    locale: context.locale,
    setLocale: context.setLocale,
    isLoading: context.isLoading,
    translations: context.translations,
  };
};

/**
 * Get a translation value by key path (e.g., 'navigation.home')
 */
function getTranslationValue(translations: Translations, key: string): string {
  const keys = key.split('.');
  let value: any = translations;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Return the key itself if translation not found
      return key;
    }
  }

  return typeof value === 'string' ? value : key;
}
async function loadTranslations(locale: SupportedLocale, themeName: string = 'cray'): Promise<Translations> {
  try {
    // Try to load from theme directory
    const response = await fetch(`/themes/${themeName}/languages/${locale}.json`);

    if (!response.ok) {
      throw new Error(`Failed to load translations for locale: ${locale}`);
    }

    const translations = await response.json();
    logger.debug({ msg: 'Translations loaded', locale, theme: themeName });
    return translations;
  } catch (error) {
    logger.error({ msg: 'Failed to load translations', locale, error });

    // Try to load theme manifest to get default language for fallback
    try {
      const manifestResponse = await fetch(`/api/theme/${themeName}/manifest`);
      if (manifestResponse.ok) {
        const manifest = await manifestResponse.json();
        const defaultLanguage = manifest.config?.defaultLanguage;

        if (defaultLanguage && defaultLanguage !== locale && (defaultLanguage === 'en' || defaultLanguage === 'es')) {
          logger.debug({ msg: 'Attempting fallback to theme default language', defaultLanguage });
          try {
            const fallbackResponse = await fetch(`/themes/${themeName}/languages/${defaultLanguage}.json`);
            if (fallbackResponse.ok) {
              const fallbackTranslations = await fallbackResponse.json();
              logger.debug({ msg: 'Fallback translations loaded', locale: defaultLanguage, theme: themeName });
              return fallbackTranslations;
            }
          } catch (fallbackError) {
            logger.error({ msg: 'Failed to load fallback translations', locale: defaultLanguage, error: fallbackError });
          }
        }
      }
    } catch (manifestError) {
      logger.error({ msg: 'Failed to load theme manifest for fallback', error: manifestError });
    }

    // Fallback to default English translations
    return getDefaultTranslations();
  }
}

/**
 * Get default English translations as fallback
 */
function getDefaultTranslations(): Translations {
  return {
    app: {
      name: 'Store',
      welcome: 'Welcome',
      description: 'Your shopping destination',
    },
    navigation: {
      home: 'Home',
      products: 'Products',
      categories: 'Categories',
      cart: 'Cart',
      account: 'Account',
      login: 'Login',
      logout: 'Logout',
      search: 'Search',
      deals: 'Deals',
      newArrivals: 'New Arrivals',
      compare: 'Compare',
      blog: 'Blog',
      support: 'Support',
      trackOrder: 'Track Order',
    },
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome to your dashboard',
      stats: {
        totalSales: 'Total Sales',
        totalOrders: 'Total Orders',
        totalCustomers: 'Total Customers',
        lowStockProducts: 'Low Stock Products',
      },
      recentOrders: 'Recent Orders',
      viewAllOrders: 'View All Orders',
    },
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      viewAll: 'View All',
      learnMore: 'Learn More',
      close: 'Close',
      submit: 'Submit',
      back: 'Back',
      next: 'Next',
    },
    product: {
      addToCart: 'Add to Cart',
      outOfStock: 'Out of Stock',
      price: 'Price',
      quantity: 'Quantity',
      viewDetails: 'View Details',
      reviews: 'Reviews',
      writeReview: 'Write a Review',
      inStock: 'In Stock',
      specifications: 'Specifications',
      description: 'Description',
    },
    cart: {
      title: 'Shopping Cart',
      empty: 'Your cart is empty',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Tax',
      shipping: 'Shipping',
      checkout: 'Checkout',
      continueShopping: 'Continue Shopping',
      removeItem: 'Remove',
      updateQuantity: 'Update',
    },
    categories: {
      title: 'Categories',
      all: 'All Categories',
      viewCategory: 'View Category',
      productsCount: 'products',
    },
    account: {
      dashboard: 'Dashboard',
      profile: 'Profile',
      orders: 'Orders',
      addresses: 'Addresses',
      wishlist: 'Wishlist',
      paymentMethods: 'Payment Methods',
      settings: 'Settings',
      signOut: 'Sign Out',
      myAccount: 'My Account',
      welcomeBack: 'Welcome Back',
      totalOrders: 'Total Orders',
      pendingOrders: 'Pending Orders',
      totalSpent: 'Total Spent',
      wishlistItems: 'Wishlist Items',
      manageInfo: 'Manage Info',
      viewManageReturns: 'View/Manage Returns',
      savedAddresses: 'Saved Addresses',
      totalOrdersCount: 'Total Orders Count',
      profileSettings: 'Profile Settings',
      orderHistory: 'Order History',
      viewTrackOrders: 'View/Track Orders',
      noOrdersFound: 'No Orders Found',
      noOrdersYet: 'No Orders Yet',
      noOrdersFilter: 'No Orders Match Your Filter',
      startShopping: 'Start Shopping',
      all: 'All',
      pending: 'Pending',
      processing: 'Processing',
      shipped: 'Shipped',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      orderNumber: 'Order Number',
      status: 'Status',
      date: 'Date',
      total: 'Total',
      items: 'Items',
      viewDetails: 'View Details',
      trackOrder: 'Track Order',
      managePersonalInfo: 'Manage Personal Info',
      editProfile: 'Edit Profile',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      documentType: 'Document Type',
      documentNumber: 'Document Number',
      preferences: 'Preferences',
      newsletter: 'Newsletter',
      notifications: 'Notifications',
      language: 'Language',
      profileUpdated: 'Profile Updated',
      updateError: 'Update Error',
      requiredField: 'Required Field',
      manageShippingBilling: 'Manage Shipping/Billing',
      addAddress: 'Add Address',
      editAddress: 'Edit Address',
      newAddress: 'New Address',
      fullName: 'Full Name',
      streetAddress: 'Street Address',
      apartment: 'Apartment',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      country: 'Country',
      deleteAddress: 'Delete Address',
      setDefault: 'Set Default',
      default: 'Default',
      shipping: 'Shipping',
      billing: 'Billing',
      savedItems: 'Saved Items',
      itemsCount: 'Items Count',
      emptyWishlist: 'Empty Wishlist',
      saveItemsLater: 'Save Items for Later',
      addToCart: 'Add to Cart',
      remove: 'Remove',
      inStock: 'In Stock',
      outOfStock: 'Out of Stock',
      managePaymentOptions: 'Manage Payment Options',
      addPaymentMethod: 'Add Payment Method',
      removePaymentMethod: 'Remove Payment Method',
      paymentSecure: 'Payment Secure',
      paymentDemo: 'Payment Demo',
      returnsRefunds: 'Returns & Refunds',
      manageReturns: 'Manage Returns',
      requestReturn: 'Request Return',
      returnPolicy: 'Return Policy',
      noReturns: 'No Returns',
      noReturnsYet: 'No Returns Yet',
      returnNumber: 'Return Number',
      refundAmount: 'Refund Amount',
      reason: 'Reason',
      returnStatuses: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        refunded: 'Refunded',
      },
      orderNotFound: '',
      orderNotFoundMessage: '',
      backToOrders: '',
      placedOn: '',
      orderItems: '',
      sku: '',
      qty: '',
      orderSummary: '',
      subtotal: '',
      paymentMethod: '',
      returns: '',
      personalInformation: '',
      cancel: '',
      saveChanges: '',
      saving: '',
      receiveNewsletter: '',
      receiveNotifications: '',
      placedOnDate: '',
      orderNumberLabel: '',
      moreItems: '',
      approved: '',
      rejected: '',
      paid: '',
      failed: '',
      in_transit: '',
      mp_processing: '',
      mp_approved: '',
      mp_pending: '',
      mp_rejected: '',
      oca_shipping_pending: '',
      oca_shipping_confirmed: '',
      oca_shipping_completed: '',
      oca_shipping_cancelled: '',
      shipping_pending: '',
      shipping_confirmed: '',
      shipping_completed: '',
      shipping_cancelled: '',
      freeShipping: '',
      discount: '',
      paymentHistory: '',
      paymentId: '',
      provider: '',
      shippingProvider: '',
      estimatedDelivery: '',
      confirmed: '',
      completed: '',
      abandoned: '',
      refunded: '',
      firstName: '',
      lastName: '',
      idType: '',
      idNumber: '',
      enterprise: '',
      taxId: '',
      birthdate: '',
      notes: '',
      customerType: '',
      username: '',
      viewInvoice: '',
      selectOption: '',
      nationalId: '',
      passport: '',
      other: '',
      selectType: '',
      regular: '',
      wholesaler: '',
      reseller: '',
    },
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      createAccount: 'Create Account',
      forgotPassword: {
        title: 'Forgot Password',
        description: "Enter your email address and we'll send you a link to reset your password.",
        errorSendingEmail: 'Error sending reset email',
        emailSent: 'Check your email',
        emailSentMessage: "We've sent a password reset link to",
        backToLogin: 'Back to login',
        sending: 'Sending...',
        sendResetLink: 'Send reset link',
      },
      resetPassword: {
        title: 'Reset Password',
        description: 'Enter your new password',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 6 characters',
        updateSuccess: 'Password updated successfully',
        updateError: 'Error updating password',
        passwordUpdated: 'Password updated',
        passwordUpdatedMessage: 'Your password has been updated successfully',
        newPassword: 'New password',
        confirmNewPassword: 'Confirm new password',
        updating: 'Updating...',
        updatePassword: 'Update password',
      },
      verifyEmail: {
        verifying: 'Verifying...',
        verifyingMessage: 'Please wait while we verify your email',
        verified: 'Email verified',
        verifiedMessage: 'Your email has been verified successfully',
        invalidToken: 'Invalid verification token',
        verificationSuccess: 'Email verified successfully',
        verificationError: 'Error verifying email',
        backToLogin: 'Back to login',
      },
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      rememberMe: 'Remember Me',
      wholesaler: 'Wholesale',
      reseller: 'Retail',
      loggedin: ''
    },
    checkout: {
      title: 'Checkout',
      shipping: 'Shipping',
      payment: 'Payment',
      review: 'Review',
      placeOrder: 'Place Order',
      orderSummary: 'Order Summary',
      shippingAddress: 'Shipping Address',
      billingAddress: 'Billing Address',
      creditCard: 'Credit Card',
      paypal: 'PayPal',
      bankTransfer: 'Bank Transfer',
      debitCard: 'Debit Card',
    },
    footer: {
      shop: 'Shop',
      myAccount: 'My Account',
      customerSupport: 'Customer Support',
      company: 'Company',
      legal: 'Legal',
      followUs: 'Follow Us',
      newsletter: 'Newsletter',
      subscribeNewsletter: 'Subscribe to our newsletter',
    },
  };
}

/**
 * Get browser or system locale
 */
function getBrowserLocale(): SupportedLocale {
  if (typeof window === 'undefined') return 'es';

  const locale = navigator.language.split('-')[0];
  return (locale === 'es' ? 'es' : 'en') as SupportedLocale;
}

/**
 * Get saved locale from sessionStorage
 */
function getSavedLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;

  const saved = sessionStorage.getItem('locale');
  return saved === 'en' || saved === 'es' ? saved : null;
}

/**
 * Save locale to sessionStorage
 */
function saveLocale(locale: SupportedLocale) {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('locale', locale);
  }
}

export interface TranslationProviderProps {
  children: ReactNode;
  themeName?: string;
  initialLocale?: SupportedLocale;
}

/**
 * Translation Provider Component
 *
 * Wrap your app with this to enable translations
 *
 * @example
 * ```tsx
 * <TranslationProvider themeName="cray">
 *   <App />
 * </TranslationProvider>
 * ```
 */
export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children, themeName, initialLocale }) => {
  const config = useConfig();

  // Always call hooks in the same order
  const effectiveThemeName = themeName || config?.theme?.activeTheme || 'cray';
  const effectiveInitialLocale = initialLocale || (config?.i18n?.defaultLanguage as SupportedLocale) || 'es';

  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    // Priority: theme default > saved user preference > browser locale
    // Theme defines what the default should be for that theme
    return effectiveInitialLocale || getSavedLocale() || getBrowserLocale();
  });

  const [translations, setTranslations] = useState<Translations>(getDefaultTranslations());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only load translations when config is available
    if (!config) return;

    const load = async () => {
      setIsLoading(true);
      const t = await loadTranslations(locale, effectiveThemeName);
      setTranslations(t);
      setIsLoading(false);
    };

    load();
  }, [locale, effectiveThemeName, config]);

  const setLocale = (newLocale: SupportedLocale) => {
    setLocaleState(newLocale);
    saveLocale(newLocale);
    logger.debug({ msg: 'Locale changed', locale: newLocale });
  };

  const value: TranslationContextType = {
    locale,
    setLocale,
    t: (key: string) => getTranslationValue(translations, key),
    translations,
    isLoading,
  };

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};
